import React from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Flex,
  Spinner,
  Center,
  Divider,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { KPIConfiguration } from 'src/components/molecules/KPIConfiguration';
import { ModelHeader } from 'src/components/molecules/ModelHeader';
import { ModelTypeRadio } from 'src/components/molecules/ModelTypeRadio';
import { useModelForm } from '../../hooks/useModelForm';
import { MediaSelectList } from '../molecules/MediaSelectList';
import { CampaignSelectList } from '../molecules/CampaignSelectList';
import { LtParentSelect } from '../molecules/LtParentSelect';

type CreateModelPageProps = {
  onComplete: () => void;
};

export const CreateModelPage: React.FC<CreateModelPageProps> = ({
  onComplete,
}) => {
  const {
    models,
    kpiSources,
    kpiMetrics,
    mediaPlatforms,
    campaignsWithGroups,
    eligibleLtParents,
    ltDisabledReason,
    isLoading,
    isSubmitting,
    areAllModelsValid,
    toastState,
    hideToast,
    handleModelNameChange,
    handleModelTypeChange,
    handleParentModelChange,
    handleSourceChange,
    handleSourceReset,
    handleMetricChange,
    handleMetricReset,
    handleUnitChange,
    handleUnitReset,
    handleMediaChange,
    handleCampaignSelectionsChange,
    handleAddModel,
    handleRemoveModel,
    handleSubmit,
    handleCancel,
  } = useModelForm({ mode: 'create', onComplete });

  if (isLoading && models[0].modelName === '') {
    return (
      <Center py={20}>
        <Spinner color="icon.green" size="lg" />
      </Center>
    );
  }

  return (
    <>
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />
      <Box
        bg="surface.panel"
        borderRadius="16px"
        border="1px solid"
        borderColor="surface.border"
        w="100%"
        p={6}>
        {/* Page Title */}
        <Text fontSize="16px" fontWeight={600} color="content.title" mb={6}>
          {models.length > 1 ? 'CREATE NEW MODELS' : 'CREATE NEW MODEL'}
        </Text>

        {models.map((model, index) => (
          <Box key={model.id} mb={index < models.length - 1 ? 8 : 0}>
            {/* Model Header (for multiple models) */}
            <ModelHeader
              modelIndex={index}
              modelsLength={models.length}
              onRemove={() => handleRemoveModel(model.id)}
              canRemove={models.length > 1}
            />

            <VStack spacing={6} align="stretch">
              {/* Model Name */}
              <Box>
                <FormControl>
                  <FormLabel
                    fontSize="16px"
                    fontWeight={500}
                    color="content.title"
                    mb={3}>
                    Model name
                  </FormLabel>
                  <Input
                    placeholder="Name your model"
                    value={model.modelName}
                    onChange={(e) =>
                      handleModelNameChange(model.id, e.target.value)
                    }
                    bg="surface.panel"
                    border="1px solid"
                    borderColor="surface.border"
                    borderRadius="md"
                    py={3}
                    px={4}
                    fontSize="16px"
                    _placeholder={{ color: 'content.muted' }}
                    _focus={{
                      borderColor: 'icon.green',
                      boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                    }}
                  />
                </FormControl>
              </Box>

              <ModelTypeRadio
                value={model.modelType}
                onChange={(next) => handleModelTypeChange(model.id, next)}
                isDisabled={isLoading}
                disabledOptions={
                  ltDisabledReason ? { lt: ltDisabledReason } : undefined
                }
              />

              {model.modelType === 'lt' ? (
                <LtParentSelect
                  eligibleParents={eligibleLtParents}
                  parentModelId={model.parentModelId}
                  onChange={(parentId) =>
                    handleParentModelChange(model.id, parentId)
                  }
                  isDisabled={isLoading}
                />
              ) : (
                <>
                  <KPIConfiguration
                    model={model}
                    onSourceChange={handleSourceChange}
                    onSourceReset={handleSourceReset}
                    onMetricChange={handleMetricChange}
                    onMetricReset={handleMetricReset}
                    onUnitChange={handleUnitChange}
                    onUnitReset={handleUnitReset}
                    kpiSources={kpiSources}
                    kpiMetrics={kpiMetrics}
                    isLoading={isLoading}
                  />

                  <MediaSelectList
                    mediaPlatforms={mediaPlatforms}
                    selectedMedia={model.selectedMedia}
                    onChange={(selectedMedia) =>
                      handleMediaChange(model.id, selectedMedia)
                    }
                    isDisabled={isLoading}
                  />

                  <CampaignSelectList
                    campaigns={campaignsWithGroups}
                    selectedCampaigns={model.campaignSelections}
                    onChange={(selections) =>
                      handleCampaignSelectionsChange(model.id, selections)
                    }
                    isDisabled={isLoading || model.selectedMedia.length === 0}
                    emptyMessage={
                      model.selectedMedia.length === 0
                        ? 'Select media above to load campaigns'
                        : 'No campaigns available'
                    }
                  />
                </>
              )}
            </VStack>

            {index < models.length - 1 && (
              <Divider my={8} borderColor="surface.border" />
            )}
          </Box>
        ))}

        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify={{ base: 'stretch', md: 'flex-end' }}
          alignItems="center"
          gap={4}
          mt={8}>
          <Button
            buttonName="cancel-create-model"
            variant="outline"
            borderColor="gray.300"
            color="content.body"
            bg="surface.panel"
            borderRadius="lg"
            w={{ base: '100%', md: '160px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleCancel}
            _hover={{ bg: 'sidebar.itemHoverBg' }}>
            Cancel
          </Button>

          <Button
            buttonName="add-new-model"
            leftIcon={<Icon iconName="plus" />}
            variant="outline"
            borderColor="icon.green"
            color="content.title"
            bg="surface.panel"
            borderRadius="lg"
            w={{ base: '100%', md: '240px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleAddModel}
            _hover={{ bg: 'icon.green', color: 'white' }}>
            Add new model
          </Button>

          <Button
            buttonName="submit-create-model"
            bg="icon.green"
            color="white"
            borderRadius="lg"
            w={{ base: '100%', md: '200px' }}
            py={3}
            alignSelf="center"
            fontSize="16px"
            fontWeight={600}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Creating..."
            isDisabled={!areAllModelsValid || isSubmitting}
            _hover={{ opacity: 0.9 }}
            _disabled={{
              bg: 'icon.green',
              cursor: 'not-allowed',
              opacity: 0.5,
            }}>
            Create Model{models.length > 1 ? 's' : ''}
          </Button>
        </Flex>
      </Box>
    </>
  );
};
